import { CSSProperties } from "react";

/**
 * SYSTEM_DESIGN.md §3.2 / §6.5 — Badge de estado.
 *
 * Encapsula la "regla de oro" del §3.2: el verde de marca (#339966) NO se usa
 * para el estado "Activo". Cada tono sale de los tokens semánticos, así que un
 * badge no puede volver a pintarse con el color de marca por descuido.
 *
 * El texto es español por defecto (§6.5): no existe "Active"/"Inactive".
 */
export type StatusTone = "success" | "warning" | "pending" | "error" | "info";

export interface ToneTokens {
  bg: string;
  fg: string;
  border: string;
}

/**
 * Mapeo tono → tokens del §3.2. Exportado para poder testear la regla de oro
 * sin depender del CSS resuelto: jsdom descarta los valores `var()` en
 * background-color y color, así que el DOM renderizado no sirve para afirmarla.
 */
export const TONES: Record<StatusTone, ToneTokens> = {
  success: { bg: "--success-bg-light", fg: "--success-bg-dark", border: "--success-default" },
  warning: { bg: "--warning-bg-light", fg: "--warning-bg-dark", border: "--warning-default" },
  pending: {
    bg: "--warning-alt-bg-light",
    fg: "--warning-alt-bg-dark",
    border: "--warning-alt-default",
  },
  error: { bg: "--error-bg-light", fg: "--error-bg-dark", border: "--error-default" },
  info: { bg: "--info-bg-light", fg: "--info-bg-dark", border: "--info-default" },
};

interface StatusBadgeProps {
  /** Texto a mostrar. En español, siempre (§6.5). */
  label: string;
  tone: StatusTone;
  className?: string;
}

export const StatusBadge = ({ label, tone, className }: StatusBadgeProps) => {
  const t = TONES[tone];
  const style: CSSProperties = {
    backgroundColor: `var(${t.bg})`,
    color: `var(${t.fg})`,
    border: `1px solid var(${t.border})`,
    borderRadius: "var(--radius-md)",
    padding: "2px 10px",
    fontSize: "0.8125rem",
    fontWeight: 600,
    lineHeight: 1.5,
    display: "inline-block",
    whiteSpace: "nowrap",
  };
  return (
    <span style={style} className={className}>
      {label}
    </span>
  );
};

/**
 * Atajo para el caso más común del backoffice: activo / inactivo.
 * Acepta el género para "Activa/Inactiva" (categorías, etc.).
 */
export const ActiveBadge = ({
  active,
  gender = "m",
}: {
  active: boolean;
  gender?: "m" | "f";
}) => {
  const suffix = gender === "f" ? "a" : "o";
  return (
    <StatusBadge
      tone={active ? "success" : "error"}
      label={active ? `Activ${suffix}` : `Inactiv${suffix}`}
    />
  );
};

/**
 * Puente desde las `severity` de PrimeReact a los tonos del §3.2, para migrar
 * los helpers que ya existen (getBenefitStatusSeverity, getVenueStatusSeverity)
 * sin reescribirlos.
 */
export const severityToTone = (
  severity?: "success" | "danger" | "warning" | "info" | null
): StatusTone => {
  switch (severity) {
    case "success":
      return "success";
    case "danger":
      return "error";
    case "warning":
      return "warning";
    case "info":
      return "info";
    default:
      return "info";
  }
};
